/**
 * Dashboard overview.
 *
 *   - Merchant user (external): scoped to their merchant.
 *   - Internal user / superuser: platform-wide aggregate across every merchant.
 *
 * The backend (`GET /api/overview/summary` + `/api/overview/charts`) already
 * applies the scope rule — this page just renders what comes back. The
 * `scope` field returned by the API drives the page label.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconChartLine,
  IconClock,
  IconCoin,
  IconList,
  IconRefresh,
  IconWallet,
} from '@tabler/icons-react';
import { overviewApi } from '../api/overview';
import { useProfile } from '../auth/SessionContext';
import { useTranslation } from '../i18n/LocaleContext';
import { formatIdr } from '../lib/format';
import { ApiCallError } from '../api/client';

import '@mantine/charts/styles.css';

type StatProps = {
  label: string;
  value: string;
  hint?: string;
  icon: typeof IconWallet;
  color?: string;
};

function StatCard({ label, value, hint, icon: Icon, color = 'brand' }: StatProps) {
  return (
    <Card>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text c="dimmed" size="xs" tt="uppercase" fw={600}>{label}</Text>
          <Text size="xl" fw={700}>{value}</Text>
          {hint && <Text c="dimmed" size="xs">{hint}</Text>}
        </Stack>
        <ThemeIcon color={color} variant="light" size={40} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

/**
 * Categorical colour palette for chart series — one distinct hue per line.
 *
 * These are explicit hex values (NOT theme keys) chosen for MAXIMUM adjacent
 * contrast so neighbouring methods never look alike. The previous palette led
 * with `brand.6` (#2e8be6) then `blue.6` (#228be6) — nearly the same blue — so
 * the first two method lines rendered as one colour. Ordered so consecutive
 * entries jump hue (blue → orange → green → red → purple …); cycles after 12.
 */
const SERIES_COLORS = [
  '#4263eb', // blue
  '#f76707', // orange
  '#2f9e44', // green
  '#e03131', // red
  '#ae3ec9', // purple
  '#0ca678', // teal
  '#e64980', // pink
  '#f08c00', // amber
  '#1098ad', // cyan
  '#74b816', // lime
  '#7048e8', // violet
  '#d6336c', // magenta
];

export function DashboardPage() {
  const profile = useProfile();
  const { t } = useTranslation();

  const summaryQ = useQuery({
    queryKey: ['overview', 'summary'],
    queryFn: () => overviewApi.summary(),
    refetchInterval: 10_000,
    retry: 0,
  });
  const chartsQ = useQuery({
    queryKey: ['overview', 'charts'],
    queryFn: () => overviewApi.charts(),
    refetchInterval: 60_000,
    retry: 0,
  });

  const summary = summaryQ.data;
  const charts = chartsQ.data;

  const scopeLabel = useMemo(() => {
    if (summary?.scope === 'platform') return t('dashboard.scopePlatform');
    if (summary?.scope === 'merchant') return t('dashboard.scopeMerchant');
    return null;
  }, [summary, t]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>{t('dashboard.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('dashboard.welcome', { name: profile.accountName })}
            {scopeLabel && (
              <Badge ml="sm" variant="light" color={summary?.scope === 'platform' ? 'blue' : 'brand'}>
                {scopeLabel}
              </Badge>
            )}
          </Text>
        </div>
        <Group gap="xs">
          <Badge variant="light" color="gray">GMT+7</Badge>
          <Tooltip label={t('common.reload')}>
            <ActionIcon
              variant="default"
              loading={summaryQ.isRefetching || chartsQ.isRefetching}
              onClick={() => {
                void summaryQ.refetch();
                void chartsQ.refetch();
              }}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {summaryQ.error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {summaryQ.error instanceof ApiCallError
            ? `${summaryQ.error.code}: ${summaryQ.error.message}`
            : t('dashboard.overviewFailedToLoad')}
        </Alert>
      )}

      {/* ============ Balance Overview ============ */}
      <Card>
        <Stack gap="sm">
          <Group gap={6}>
            <IconWallet size={18} />
            <Text fw={600}>{t('dashboard.balanceOverview')}</Text>
            {summary?.scope === 'platform' && (
              <Text size="xs" c="dimmed">{t('dashboard.balanceOverviewHint')}</Text>
            )}
          </Group>
          {summaryQ.isLoading ? (
            <Group justify="center" py="md"><Loader size="sm" /></Group>
          ) : !summary?.visibility.canSeeBalances && !summary?.visibility.canSeeCounts ? (
            <Alert color="gray" variant="light">{t('dashboard.balancesHidden')}</Alert>
          ) : (
            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <StatCard
                  label={t('dashboard.pendingBalance')}
                  value={summary?.balance ? formatIdr(summary.balance.pending) : '—'}
                  hint={t('dashboard.pendingBalanceHint')}
                  icon={IconClock}
                  color="yellow"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <StatCard
                  label={t('dashboard.activeBalance')}
                  value={summary?.balance ? formatIdr(summary.balance.active) : '—'}
                  hint={t('dashboard.activeBalanceHint')}
                  icon={IconWallet}
                  color="green"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <StatCard
                  label={t('dashboard.todayTrxCount')}
                  value={
                    summary?.todayTrxCount != null
                      ? summary.todayTrxCount.toLocaleString('id-ID')
                      : '—'
                  }
                  hint={t('dashboard.todayTrxCountHint')}
                  icon={IconList}
                  color="blue"
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Card>

      {/* ============ Total Overview ============ */}
      <Card>
        <Stack gap="sm">
          <Group gap={6}>
            <IconCoin size={18} />
            <Text fw={600}>{t('dashboard.totalOverview')}</Text>
            <Text size="xs" c="dimmed">{t('dashboard.totalOverviewHint')}</Text>
          </Group>
          {summaryQ.isLoading ? (
            <Group justify="center" py="md"><Loader size="sm" /></Group>
          ) : summary?.totals ? (
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <StatCard
                  label={t('dashboard.totalDeposit')}
                  value={formatIdr(summary.totals.deposit.amount)}
                  hint={t('dashboard.totalDepositHint')}
                  icon={IconArrowDown}
                  color="green"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <StatCard
                  label={t('dashboard.totalCountDeposit')}
                  value={summary.totals.deposit.count.toLocaleString('id-ID')}
                  hint={t('dashboard.totalCountDepositHint')}
                  icon={IconList}
                  color="green"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <StatCard
                  label={t('dashboard.totalDisbursement')}
                  value={formatIdr(summary.totals.disbursement.amount)}
                  hint={t('dashboard.totalDisbursementHint')}
                  icon={IconArrowUp}
                  color="red"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <StatCard
                  label={t('dashboard.totalCountCashOut')}
                  value={summary.totals.disbursement.count.toLocaleString('id-ID')}
                  hint={t('dashboard.totalCountCashOutHint')}
                  icon={IconList}
                  color="red"
                />
              </Grid.Col>
            </Grid>
          ) : (
            <Alert color="gray" variant="light">{t('dashboard.totalsHidden')}</Alert>
          )}
        </Stack>
      </Card>

      {/* ============ Charts ============ */}
      <Card>
        <Stack gap="md">
          <Group gap={6}>
            <IconChartLine size={18} />
            <Text fw={600}>{t('dashboard.charts')}</Text>
            {charts && (
              <Text size="xs" c="dimmed">
                {t('dashboard.chartRangeLabel', { from: charts.range.from, to: charts.range.to })}
              </Text>
            )}
          </Group>

          {chartsQ.error && (
            <Alert color="gray" icon={<IconAlertTriangle size={14} />} variant="light">
              {chartsQ.error instanceof ApiCallError && chartsQ.error.code === 'permission_denied'
                ? t('dashboard.chartsNeedPermission')
                : chartsQ.error instanceof ApiCallError
                  ? `${chartsQ.error.code}: ${chartsQ.error.message}`
                  : t('dashboard.chartsFailedToLoad')}
            </Alert>
          )}

          {chartsQ.isLoading && (
            <Group justify="center" py="xl"><Loader /></Group>
          )}

          {charts && !chartsQ.error && (
            <Stack gap="lg">
              {/* Chart 1: Total amount deposit & disbursement per day */}
              <div>
                <Text size="sm" fw={600} mb={4}>{t('dashboard.chartAmountByDay')}</Text>
                <LineChart
                  h={280}
                  data={charts.amountByDay}
                  dataKey="date"
                  withLegend
                  withTooltip
                  tooltipAnimationDuration={150}
                  curveType="monotone"
                  valueFormatter={(v) => formatIdr(v)}
                  series={[
                    { name: 'depositAmount', label: t('dashboard.deposit'), color: 'green.6' },
                    { name: 'disbursementAmount', label: t('dashboard.disbursement'), color: 'red.6' },
                  ]}
                />
              </div>

              {/* Chart 2: Deposit count per day, one line per method */}
              <div>
                <Text size="sm" fw={600} mb={4}>{t('dashboard.chartDepositCountByMethod')}</Text>
                {charts.depositCountByMethod.methods.length === 0 ? (
                  <Text size="xs" c="dimmed" py="md" ta="center">{t('dashboard.chartEmptyDeposit')}</Text>
                ) : (
                  <LineChart
                    h={280}
                    data={charts.depositCountByMethod.data}
                    dataKey="date"
                    withLegend
                    withTooltip
                    curveType="monotone"
                    series={charts.depositCountByMethod.methods.map((m, idx) => ({
                      name: m,
                      label: m,
                      color: SERIES_COLORS[idx % SERIES_COLORS.length]!,
                    }))}
                  />
                )}
              </div>

              {/* Chart 3: Disbursement count per day, one line per method */}
              <div>
                <Text size="sm" fw={600} mb={4}>{t('dashboard.chartDisbursementCountByMethod')}</Text>
                {charts.disbursementCountByMethod.methods.length === 0 ? (
                  <Text size="xs" c="dimmed" py="md" ta="center">{t('dashboard.chartEmptyDisbursement')}</Text>
                ) : (
                  <LineChart
                    h={280}
                    data={charts.disbursementCountByMethod.data}
                    dataKey="date"
                    withLegend
                    withTooltip
                    curveType="monotone"
                    series={charts.disbursementCountByMethod.methods.map((m, idx) => ({
                      name: m,
                      label: m,
                      color: SERIES_COLORS[idx % SERIES_COLORS.length]!,
                    }))}
                  />
                )}
              </div>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
